import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  FolderKanban, Users, DollarSign, BarChart3, TrendingUp,
  AlertTriangle, CheckCircle2, Clock, Film, Activity, Building2, Bell
} from 'lucide-react';
import WorkforceBadge from '@/components/WorkforceBadge';
import { fmtMinSec } from '@/pages/AdminAllotmentPage';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors animate-fade-in" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-heading font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function ProjectCard({ project, onClick }) {
  const progress = project.shot_count > 0 ? Math.round((project.completed_shots / project.shot_count) * 100) : 0;
  const budgetUsed = project.budget > 0 ? Math.round((project.budget_spent / project.budget) * 100) : 0;
  return (
    <div
      className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-all cursor-pointer group animate-fade-in"
      data-testid={`project-card-${project.id}`}
      onClick={() => onClick?.(project)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">{project.name}</h3>
          <p className="text-[11px] text-zinc-500">{project.client_name}</p>
        </div>
        <span className={`status-${project.status} px-2 py-0.5 rounded-full text-[10px] font-medium`}>{project.status}</span>
      </div>
      <p className="text-xs text-zinc-600 mb-3 line-clamp-2">{project.description}</p>
      <div className="space-y-2">
        <div>
          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
            <span>Shots: {project.completed_shots}/{project.shot_count}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
            <span className="font-mono">Budget: {(project.budget_spent / 100000).toFixed(1)}L / {(project.budget / 100000).toFixed(1)}L</span>
            <span>{budgetUsed}%</span>
          </div>
          <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${budgetUsed > 80 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${budgetUsed}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function RecentTasks({ tasks }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm animate-fade-in">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Recent Tasks</h3>
        <Activity className="w-3.5 h-3.5 text-zinc-600" />
      </div>
      <div className="divide-y divide-zinc-800/50">
        {tasks.slice(0, 8).map(task => (
          <div key={task.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-800/30 transition-colors" data-testid={`task-row-${task.id}`}>
            <div className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: task.status === 'completed' ? '#10b981' : task.status === 'in_progress' ? '#3b82f6' : task.status === 'blocked' ? '#ef4444' : '#71717a'
            }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-300 truncate">{task.name}</p>
              <p className="text-[10px] text-zinc-600">{task.assignee_name || 'Unassigned'}</p>
            </div>
            <span className={`status-${task.status} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{task.status?.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'master_admin';
  const headerTitle = isAdmin ? 'Command Center' : 'Production Manager';
  const headerSub = isAdmin
    ? 'VawworldOS Production Overview'
    : 'Your projects, tasks, and approvals — scoped to projects you have access to.';
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workload, setWorkload] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/projects'),
      api.get('/tasks'),
      api.get('/animator/allotment/admin/workload-summary').catch(() => ({ data: null })),
    ]).then(([s, p, t, w]) => {
      setStats(s.data);
      setProjects(p.data);
      setTasks(t.data);
      setWorkload(w.data);
    }).catch(console.error);
  }, [api]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading VawworldOS...</div></div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">{headerTitle}</h1>
        <p className="text-xs text-zinc-500 mt-1">{headerSub}</p>
      </div>

      {/* Vendor Approval Requests — admin-only */}
      {isAdmin && stats.pending_vendors > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-amber-400" />
            <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Vendor Approval Pending ({stats.pending_vendors})</h3>
          </div>
          <div className="space-y-2">
            {(stats.pending_vendor_list || []).map(v => (
              <div key={v.id} className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2.5">
                <div>
                  <p className="text-xs text-zinc-200 font-medium">{v.company_name || v.full_name}</p>
                  <p className="text-[10px] text-zinc-600">{v.email} | {v.entity_type} | Team: {v.total_team_size || 0}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => { await api.put(`/vendor/${v.id}/approve`); window.location.reload(); }}
                    className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] rounded-sm">Approve</button>
                  <button onClick={async () => { await api.put(`/vendor/${v.id}/reject`, { reason: 'Rejected by admin' }); window.location.reload(); }}
                    className="h-7 px-3 bg-red-600 hover:bg-red-500 text-white text-[10px] rounded-sm">Reject</button>
                  <a href="/vendors" className="h-7 px-3 bg-zinc-800 text-zinc-300 text-[10px] rounded-sm flex items-center">View Details</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={FolderKanban} label="Projects" value={stats.active_projects} sub={`${stats.total_projects} total`} color="#3b82f6" />
        <StatCard icon={Film} label="Tasks" value={stats.total_tasks} sub={`${stats.completed_tasks} completed`} color="#06b6d4" />
        <StatCard icon={AlertTriangle} label="Blocked" value={stats.blocked_tasks} sub="Require attention" color="#ef4444" />
        {isAdmin
          ? <StatCard icon={Users} label="Freelancers" value={stats.total_freelancers} sub={`${stats.total_users} total users`} color="#f59e0b" />
          : <StatCard icon={CheckCircle2} label="Completed" value={stats.completed_tasks || 0} sub="across your projects" color="#10b981" />}
      </div>

      {/* ── Animator / Vendor / Freelancer Workload — founder ask
          Feb 14, 2026: "in our dashboard, we want to see how many secs
          we alloted to each animator". Studio-wide totals (all projects).
          Per-project breakdown lives at /allotment → CBA tab. */}
      {workload && workload.rows && workload.rows.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm animate-fade-in"
             data-testid="dashboard-workload-widget">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-heading font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-emerald-400" />
                Per-Artist Workload
              </h3>
              <p className="text-[10px] text-zinc-500 mt-0.5">
                {workload.total_artists} artists · {fmtMinSec(workload.total_seconds)} total · {workload.total_tasks} layout/animation tasks
              </p>
            </div>
            <a href="/allotment"
               className="text-[10px] text-blue-400 hover:underline"
               data-testid="dashboard-workload-open-cba">
              Open CBA Allotment →
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950/60">
                <tr className="text-[9px] uppercase tracking-wider text-zinc-500">
                  <th className="px-4 py-2 text-left font-medium">Artist</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-right font-medium" title="Total assigned time">Time</th>
                  <th className="px-3 py-2 text-right font-medium" title="Total ffprobed frames">Frames</th>
                  <th className="px-3 py-2 text-right font-medium">Tasks</th>
                  <th className="px-3 py-2 text-right font-medium" title="Distinct projects">Projects</th>
                </tr>
              </thead>
              <tbody>
                {workload.rows.slice(0, 12).map(r => {
                  const lay = (r.by_type || []).filter(t => t === 'layout').length;
                  const ani = (r.by_type || []).filter(t => t === 'animation').length;
                  return (
                    <tr key={r.user_id}
                        className="border-b border-zinc-800/30 hover:bg-zinc-800/20"
                        data-testid={`dashboard-workload-row-${r.user_id}`}>
                      <td className="px-4 py-2 text-zinc-200">
                        {r.full_name || '—'}
                        {r.department && <span className="text-[9px] text-zinc-600 ml-2">{r.department}</span>}
                      </td>
                      <td className="px-3 py-2"><WorkforceBadge user={r} /></td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-300">
                        {fmtMinSec(r.assigned_seconds)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-400">
                        {r.assigned_frames > 0 ? r.assigned_frames.toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-300">
                        {r.assigned_tasks}
                        {lay > 0 && ani > 0 && (
                          <span className="text-[9px] text-zinc-500 ml-1">L:{lay} A:{ani}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-400">{r.project_count || 1}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {workload.rows.length > 12 && (
              <div className="px-4 py-2 text-[10px] text-zinc-500 text-center border-t border-zinc-800/30">
                Showing top 12 of {workload.rows.length} artists by allocated time. Open CBA Allotment to see all.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Projects + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-sm font-heading font-semibold text-zinc-300 uppercase tracking-wider">Active Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {projects.filter(p => p.status === 'active').map(p => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        </div>
        <div>
          <RecentTasks tasks={tasks} />
        </div>
      </div>
    </div>
  );
}
